import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd'
import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'

import {
  getQuoteProvisionsCategories,
  QuoteProvisionsCategoriesType,
} from './utils/utils'
import QuoteSectionBodyTableHeader from '../quote-section-body-table-header/QuoteSectionBodyTableHeader'
import QuoteSectionBodyTableRow, {
  IQuoteSectionBodyTableRowProps,
} from '../quote-section-body-table-row/QuoteSectionBodyTableRow'
import { getUpdateQuoteTableRowType } from '../quote-section-body-table-row/utils/utils'
import { useGetFormattedClientPricingItems } from '../useGetFormattedClientPricingItems'
import { ClientPricingItemOutputDto } from '../../../../../../../../../lib/graphql'
import {
  Table,
  TableBody,
} from '../../../../../../../../../shadcn/components/ui/table'
import { errorToast } from '../../../../../../../../../utils'
import { useVatStatus } from '../../../../../../../../contexts/vat-status-context/useVatStatus'
import { IProposalInteractionsProps } from '../../../../../../../../organisms/proposal/Proposal'

const QuoteSectionBodyTableSummary = ({
  clientPricingItems,
  tableRowActions,
  ownerActions,
  clientProposalId,
  isDragDisabled,
}: {
  clientPricingItems: QuoteProvisionsCategoriesType['clientPricingItems']
  tableRowActions: IQuoteSectionBodyTableRowProps['actions']
  ownerActions: IProposalInteractionsProps['ownerActions']
  clientProposalId: string
  isDragDisabled: boolean
}) => {
  const intl = useIntl()
  const { vatStatus } = useVatStatus()

  const sections = getQuoteProvisionsCategories({ clientPricingItems })

  const [sortedItemsIdsBySections, setSortedItemsIdsBySections] =
    React.useState<string[][]>(
      sections.map((section) =>
        section.clientPricingItems.map((item) => item._id),
      ),
    )

  const { getFormattedClientPricingItems } = useGetFormattedClientPricingItems()

  const reorder = (list: string[], startIndex: number, endIndex: number) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
  }

  useEffect(() => {
    const defaultSortedItemsIdsBySections = sections.map((section) =>
      section.clientPricingItems.map((item) => item._id),
    )
    if (
      JSON.stringify(defaultSortedItemsIdsBySections) !==
      JSON.stringify(sortedItemsIdsBySections)
    ) {
      setSortedItemsIdsBySections(defaultSortedItemsIdsBySections)
    }
  }, [clientPricingItems])

  const onDragEnd = ({
    result,
    sectionIndex,
  }: {
    result: DropResult
    sectionIndex: number
  }): void => {
    if (!result.destination) {
      return
    }

    const items = reorder(
      sortedItemsIdsBySections[sectionIndex],
      result.source.index,
      result.destination.index,
    )

    setSortedItemsIdsBySections((sections) => {
      const newSections = [...sections]
      newSections[sectionIndex] = items
      return newSections
    })
  }

  useEffect(() => {
    const updateQuoteProvisionsPositions = async () => {
      const filteredItems = sortedItemsIdsBySections.flat()
      const defaultSortedItemsIdsBySections = sections.map((section) =>
        section.clientPricingItems.map((item) => item._id),
      )
      try {
        if (
          JSON.stringify(defaultSortedItemsIdsBySections) ===
          JSON.stringify(sortedItemsIdsBySections)
        ) {
          return
        }
        await ownerActions?.ownerUpdatedQuoteProvisionsPositions.callback({
          clientProposalId,
          clientPricingItemsIds: filteredItems,
        })
      } catch {
        errorToast([
          intl.formatMessage({
            id: 'ui.UpdateQuoteTable.error',
            defaultMessage:
              "Une erreur est survenue lors de la modification de l'ordre des prestations",
          }),
        ])
        if (
          JSON.stringify(defaultSortedItemsIdsBySections) !==
          JSON.stringify(sortedItemsIdsBySections)
        ) {
          setSortedItemsIdsBySections(defaultSortedItemsIdsBySections)
        }
      }
    }

    updateQuoteProvisionsPositions()
  }, [sortedItemsIdsBySections])

  return (
    <Table>
      {sections.map((section, index) => {
        const filteredItems: ClientPricingItemOutputDto[] = (
          sortedItemsIdsBySections[index] || []
        )
          .map((id) =>
            section.clientPricingItems.find((item) => item._id === id),
          )
          .filter(
            (item): item is ClientPricingItemOutputDto => item !== undefined,
          )

        const formattedItems = getFormattedClientPricingItems({
          clientPricingItems: filteredItems,
        })
        return (
          <React.Fragment key={section.label}>
            <QuoteSectionBodyTableHeader
              icon={section.icon}
              label={section.label}
              vatStatus={vatStatus}
              hasActions={tableRowActions.length > 0}
              isDragDisabled={isDragDisabled}
            />

            <DragDropContext
              onDragEnd={(result: DropResult) =>
                onDragEnd({
                  result,
                  sectionIndex: index,
                })
              }
            >
              <Droppable droppableId={section.label}>
                {(provided) => (
                  <TableBody
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {formattedItems.map((item, index) => (
                      <Draggable
                        key={item.id}
                        draggableId={item.id}
                        index={index}
                        isDragDisabled={isDragDisabled}
                      >
                        {(provided) => (
                          <QuoteSectionBodyTableRow
                            key={item.id}
                            {...item}
                            actions={tableRowActions}
                            type={getUpdateQuoteTableRowType(
                              item.adjustmentStatus,
                            )}
                            draggableProvided={provided}
                            isDragDisabled={isDragDisabled}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </DragDropContext>
          </React.Fragment>
        )
      })}
    </Table>
  )
}

export default QuoteSectionBodyTableSummary
